// AUD-1 — the WebAudio playback engine. This is the ONLY audio file that touches
// WebAudio/DOM (ENG-1 keeps the sim and the synth pure). Graph: each voice →
// PannerNode (equalpower, HRTF off) → bus gain (sound|music) → master gain →
// destination, with the three gains driven by the options sliders. Patches are
// rendered to PCM by the pure synth (`synth/patch`) and cached as AudioBuffers.
//
// A rolling **event log** records every play regardless of whether the context is
// actually producing sound (autoplay policy may keep it suspended until a user
// gesture). E2E and the subtitles overlay read that log — the audible output is a
// side effect, the log is the deterministic seam.
import { renderPatch, type Patch } from './synth/patch';
import { patchForOrDefault } from './synth/sfx';
import { MUSIC_VOICES, type MusicNote } from './synth/music';

/** A logged audio event (subtitles + E2E read these). */
export interface AudioEvent {
  readonly bus: 'sound' | 'music';
  readonly id: string;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly pitch: number;
  /** Monotonic sequence number (stable, gesture-independent). */
  readonly seq: number;
}

export interface PlayOptions {
  readonly x?: number;
  readonly y?: number;
  readonly z?: number;
  readonly pitch?: number;
  readonly volume?: number;
}

type MaybeCtx = AudioContext | null;

const AC: typeof AudioContext | undefined =
  typeof AudioContext !== 'undefined'
    ? AudioContext
    : (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;

/** The positional WebAudio engine. Degrades to log-only when no context exists. */
export class AudioEngine {
  private ctx: MaybeCtx = null;
  private master: GainNode | null = null;
  private soundBus: GainNode | null = null;
  private musicBus: GainNode | null = null;
  private readonly buffers = new Map<string, AudioBuffer>();

  private masterVol = 1;
  private soundVol = 1;
  private musicVol = 0.6;

  private readonly log: AudioEvent[] = [];
  private seq = 0;
  private readonly maxLog: number;

  /** Fired for every play (subtitles overlay subscribes). */
  onEvent: ((e: AudioEvent) => void) | null = null;

  constructor(maxLog = 256) { this.maxLog = maxLog; }

  /** Lazily build the graph. Safe to call repeatedly; a no-op without WebAudio. */
  private ensure(): boolean {
    if (this.ctx) return true;
    if (!AC) return false;
    try {
      const ctx = new AC();
      const master = ctx.createGain();
      const soundBus = ctx.createGain();
      const musicBus = ctx.createGain();
      master.gain.value = this.masterVol;
      soundBus.gain.value = this.soundVol;
      musicBus.gain.value = this.musicVol;
      soundBus.connect(master);
      musicBus.connect(master);
      master.connect(ctx.destination);
      this.ctx = ctx; this.master = master; this.soundBus = soundBus; this.musicBus = musicBus;
      return true;
    } catch {
      return false;
    }
  }

  /** Resume the context (call from a user-gesture handler; autoplay policy). */
  resume(): void {
    if (this.ensure()) void this.ctx!.resume();
  }

  // ── Options sliders (0..1) ──
  setMasterVolume(v: number): void { this.masterVol = clamp01(v); if (this.master) this.master.gain.value = this.masterVol; }
  setSoundVolume(v: number): void { this.soundVol = clamp01(v); if (this.soundBus) this.soundBus.gain.value = this.soundVol; }
  setMusicVolume(v: number): void { this.musicVol = clamp01(v); if (this.musicBus) this.musicBus.gain.value = this.musicVol; }
  get volumes(): { master: number; sound: number; music: number } {
    return { master: this.masterVol, sound: this.soundVol, music: this.musicVol };
  }

  /** Update the WebAudio listener from the camera (position + forward/up). */
  setListener(px: number, py: number, pz: number, fx: number, fy: number, fz: number): void {
    if (!this.ensure()) return;
    const l = this.ctx!.listener;
    if ('positionX' in l) {
      const t = this.ctx!.currentTime;
      l.positionX.setValueAtTime(px, t); l.positionY.setValueAtTime(py, t); l.positionZ.setValueAtTime(pz, t);
      l.forwardX.setValueAtTime(fx, t); l.forwardY.setValueAtTime(fy, t); l.forwardZ.setValueAtTime(fz, t);
      l.upX.setValueAtTime(0, t); l.upY.setValueAtTime(1, t); l.upZ.setValueAtTime(0, t);
    } else {
      // Deprecated API fallback.
      (l as unknown as { setPosition(x: number, y: number, z: number): void }).setPosition(px, py, pz);
      (l as unknown as { setOrientation(fx: number, fy: number, fz: number, ux: number, uy: number, uz: number): void })
        .setOrientation(fx, fy, fz, 0, 1, 0);
    }
  }

  /** Play a sound event by id (resolves the patch, logs, then voices positionally). */
  playSound(id: string, opts: PlayOptions = {}): void {
    this.emit('sound', id, opts);
    this.voice(this.soundBus, patchForOrDefault(id), id, opts);
  }

  /** Play an already-resolved patch (note-block voices, discs) on a chosen bus. */
  playPatch(bus: 'sound' | 'music', id: string, patch: Patch, opts: PlayOptions = {}): void {
    this.emit(bus, id, opts);
    this.voice(bus === 'music' ? this.musicBus : this.soundBus, patch, id, opts);
  }

  // ── Generative music (AUD-4) ──
  private musicSources: AudioBufferSourceNode[] = [];

  /** Play a composed note list on the music bus (positional if opts has coords).
   *  Logs one `id` event; replaces any currently-playing music. */
  playMusic(id: string, notes: readonly MusicNote[], opts: PlayOptions = {}): void {
    this.emit('music', id, opts);
    if (!this.ensure() || !this.ctx || !this.musicBus || this.ctx.state === 'suspended') return;
    this.stopMusic();
    const t0 = this.ctx.currentTime + 0.05;
    for (const n of notes) {
      const voice = MUSIC_VOICES[n.voice];
      const pcm = renderPatch({ ...voice, durationMs: n.durationMs }, { pitch: n.freq / voice.freq, sampleRate: this.ctx.sampleRate });
      const buf = this.ctx.createBuffer(1, pcm.length, this.ctx.sampleRate);
      buf.getChannelData(0).set(pcm);
      const src = this.ctx.createBufferSource();
      src.buffer = buf;
      this.connectPositioned(src, this.musicBus, opts);
      src.start(t0 + n.timeMs / 1000);
      this.musicSources.push(src);
    }
  }

  /** Stop any scheduled music (jukebox eject / context change). */
  stopMusic(): void {
    for (const s of this.musicSources) { try { s.stop(); } catch { /* already stopped */ } }
    this.musicSources = [];
  }

  private emit(bus: 'sound' | 'music', id: string, opts: PlayOptions): void {
    const e: AudioEvent = {
      bus, id, x: opts.x ?? 0, y: opts.y ?? 0, z: opts.z ?? 0, pitch: opts.pitch ?? 1, seq: this.seq++,
    };
    this.log.push(e);
    if (this.log.length > this.maxLog) this.log.shift();
    this.onEvent?.(e);
  }

  private voice(busNode: GainNode | null, patch: Patch, id: string, opts: PlayOptions): void {
    if (!this.ensure() || !this.ctx || !busNode) return;
    if (this.ctx.state === 'suspended') return; // log recorded; audio waits for a gesture
    const pitch = opts.pitch ?? 1;
    const key = `${id}|${Math.round(pitch * 100)}`;
    let buf = this.buffers.get(key);
    if (!buf) {
      const pcm = renderPatch(patch, { pitch, sampleRate: this.ctx.sampleRate });
      buf = this.ctx.createBuffer(1, pcm.length, this.ctx.sampleRate);
      buf.getChannelData(0).set(pcm);
      this.buffers.set(key, buf);
    }
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = 1; // pitch already baked into the buffer
    this.connectPositioned(src, busNode, opts);
    src.start();
  }

  /** Wire a source → (PannerNode if positional) → bus. */
  private connectPositioned(src: AudioBufferSourceNode, busNode: GainNode, opts: PlayOptions): void {
    if (!this.ctx) return;
    if (opts.x !== undefined || opts.z !== undefined) {
      const pan = this.ctx.createPanner();
      pan.panningModel = 'equalpower'; pan.distanceModel = 'inverse';
      pan.refDistance = 4; pan.maxDistance = 48; pan.rolloffFactor = 1;
      const t = this.ctx.currentTime;
      pan.positionX.setValueAtTime(opts.x ?? 0, t);
      pan.positionY.setValueAtTime(opts.y ?? 0, t);
      pan.positionZ.setValueAtTime(opts.z ?? 0, t);
      src.connect(pan); pan.connect(busNode);
    } else {
      src.connect(busNode);
    }
  }

  // ── Event log (subtitles + E2E) ──
  events(): readonly AudioEvent[] { return this.log; }
  lastEvent(): AudioEvent | null { return this.log.length ? this.log[this.log.length - 1]! : null; }
  clearEvents(): void { this.log.length = 0; }
}

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);
